import { Hero as HeroSection } from 'components/Hero';
import Layout from 'components/Layout';
import { ProductCardGrid } from 'components/ProductCardGrid';
import { graphql } from 'gatsby';
import React, { lazy, LazyExoticComponent, useEffect } from 'react';
import track from 'utils/analytics/track';
import { ANALYTICS } from 'utils/constants/analytics';
import { IContentWithImage } from 'utils/types';

import { SEO } from '../components/SEO';

const MembersSatisfaction = lazy(
  () => import('containers/HealthPlansPage/MembersSatisfaction'),
);

const HowItWorksSection = lazy(
  () => import('components/EverlyCarePage/HowItWorks'),
);
const ProductCardGridSection = lazy(() => import('components/ProductCardGrid'));
const StepCardListSection = lazy(() => import('components/StepCardList'));
const MemberComparisonSection = lazy(
  () => import('containers/HealthPlansPage/MemberComparisonSection'),
);

type Props = {
  data: {
    employersAudienceContent: {
      title: string;
      heroSection: {
        headline: string;
        backgroundColor: string;
        desktopImage: {
          url: string;
          title: string;
        };
        cta: {
          text: string;
          url: string;
        };
      };
      sections: {
        componentName: string;
        headline: string;
        disclaimer?: {
          disclaimer: string;
        };
        description: {
          description: string;
          childMarkdownRemark: {
            rawMarkdownBody: string;
          };
        };
        slug: string;
        title?: string;
        listOfContentWithImages?: IContentWithImage[];
        container?: any;
      }[];
    };
  };
};

const components: {
  HeroSection: React.FC<any>;
  MembersSatisfaction: LazyExoticComponent<React.FC<any>>;
  HowItWorksSection: LazyExoticComponent<React.FC<any>>;
  ProductCardGrid: LazyExoticComponent<React.FC<any>>;
  StepCardList: LazyExoticComponent<React.FC<any>>;
  MemberComparisonSection: LazyExoticComponent<React.FC<any>>;
} = {
  HeroSection,
  MembersSatisfaction,
  HowItWorksSection: HowItWorksSection,
  ProductCardGrid: ProductCardGridSection,
  StepCardList: StepCardListSection,
  MemberComparisonSection,
};

const isProductCardGrid = (name: string): boolean => name === 'ProductCardGrid';
const isStepCardList = (name: string): boolean => name === 'StepCardList';

export const Head = () => <SEO />;

const EmployersAudiencePage: React.FC<Props> = ({ data }) => {
  // @ts-ignore
  const { hero, sections } = data.contentfulPage001;

  useEffect(() => {
    track({
      event: ANALYTICS.EVENTS.VIEWED_EHS_PAGE,
      data: {
        label: 'EHS Employers page',
      },
    });
  }, []);

  return (
    <Layout>
      <HeroSection {...hero} />

      {sections.map(
        (
          section: Props['data']['employersAudienceContent']['sections'][number],
          i: number,
        ) => {
          const Component =
            components[section.componentName as keyof typeof components];

          if (!Component) return null;

          if (isProductCardGrid(section.componentName)) {
            return (
              <ProductCardGrid
                key={`${section.componentName}-${i}`}
                {...section}
                title={section.title ?? ''}
                cards={section.container}
                sectionHeader={{
                  ...section,
                  eyebrow: section.title || '',
                  title: section.headline || '',
                  description:
                    section.description?.childMarkdownRemark?.rawMarkdownBody,
                }}
                showOverlay={false}
                cardsPerRow={3}
              />
            );
          }

          if (isStepCardList(section.componentName)) {
            return (
              <StepCardListSection
                key={`${section.componentName}-${i}`}
                {...section}
                listOfContentWithImages={section.listOfContentWithImages ?? []}
                eyebrow={section.title || ''}
                heading={section.headline}
                description={section.description}
              />
            );
          }

          return (
            // @ts-ignore
            <Component key={`${section.componentName}-${i}`} {...section} />
          );
        },
      )}
    </Layout>
  );
};

export default EmployersAudiencePage;

export const data = graphql`
  query EmployersAudiencePageQuery {
    contentfulPage001(displayName: { eq: "Employers Page | EHS" }) {
      slug
      hero {
        ... on ContentfulHeroSection {
          eyebrow
          eyebrowColor
          headline
          description {
            childMarkdownRemark {
              rawMarkdownBody
            }
          }
          backgroundColor
          desktopImage {
            url
            title
          }
          cta {
            text
            url
          }
        }
      }
      sections {
        headline
        title
        subHeadline
        componentName
        slug
        backgroundColor
        description {
          description
        }
        disclaimer {
          childMarkdownRemark {
            html
            rawMarkdownBody
          }
        }
        sectionImage {
          title
          url
          gatsbyImageData(placeholder: BLURRED, quality: 75)
        }
        cta {
          text
          url
        }
        listOfContentWithImages {
          ... on ContentfulContentWithImage {
            heading
            description {
              childMarkdownRemark {
                rawMarkdownBody
              }
            }
            image {
              url
            }
          }
        }
        container {
          ... on ContentfulComparisonTable {
            memberA {
              title
              subtitle
              image {
                description
                gatsbyImageData(layout: CONSTRAINED, width: 400, quality: 80)
              }
            }
            memberB {
              title
              subtitle
              image {
                description
                gatsbyImageData(layout: CONSTRAINED, width: 400, quality: 80)
              }
            }
            rows {
              id
              label
              memberAContent {
                memberAContent
              }
              memberBContent {
                memberBContent
              }
              colorMemberA
              colorMemberB
            }
          }
          ... on ContentfulSlider {
            slides {
              slideHeading: heading
              description {
                childMarkdownRemark {
                  rawMarkdownBody
                }
              }
              backgroundImage {
                file {
                  url
                }
              }
              mobileBackgroundImage {
                file {
                  url
                }
              }
              ctaButton {
                url
                label
              }
              features {
                icon {
                  url
                }
                text
              }
              navigationLabel
            }
          }
          ... on ContentfulHealthSolutionCard {
            icon {
              url
            }
            title
            description
            link
          }
        }
      }
    }
  }
`;
